import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputBase,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import ViewListRounded from "@mui/icons-material/ViewListRounded";
import ViewModuleRounded from "@mui/icons-material/ViewModuleRounded";
import WhatsApp from "@mui/icons-material/WhatsApp";
import { jwtDecode } from "jwt-decode";
import AiDirectCorretor from "../features/aiDirect/components/AiDirectCorretor";
import corretorDashboardService from "../features/corretorDashboard/services/corretorDashboard.service";

const LIVIA_WIDGET_MARGIN = 28;
const LIVIA_INITIAL_BOTTOM = 96;
const LIVIA_FAB_MARGIN = 16;
const LIVIA_FAB_POSITION_STORAGE_KEY = "corretor_livia_fab_position";
const LEADS_LIMIT = 20;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getStoredLiviaFabPosition = () => {
  try {
    const value = JSON.parse(localStorage.getItem(LIVIA_FAB_POSITION_STORAGE_KEY) || "null");
    if (!Number.isFinite(value?.left) || !Number.isFinite(value?.top)) return null;
    return { left: value.left, top: value.top };
  } catch (error) {
    return null;
  }
};

const setStoredLiviaFabPosition = (position) => {
  try {
    localStorage.setItem(LIVIA_FAB_POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch (error) {
    // O botao continua reposicionavel mesmo quando o navegador bloqueia o armazenamento local.
  }
};

const getCorretorFromToken = () => {
  try {
    const token = localStorage.getItem("jwtToken") || "";
    const cleanToken = token.startsWith("Bearer ") ? token.slice(7) : token;
    const decoded = cleanToken ? jwtDecode(cleanToken) : {};

    return {
      nome: decoded?.nome || decoded?.name || "Corretor",
      email: decoded?.email || decoded?.sub || "",
      idClienteAipim:
        decoded?.id_cliente ||
        decoded?.idCliente ||
        decoded?.idClienteAipim ||
        decoded?.cliente_id ||
        null,
    };
  } catch (error) {
    return { nome: "Corretor", email: "", idClienteAipim: null };
  }
};

const formatNumber = (value) => new Intl.NumberFormat("pt-BR").format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "Ainda nao atualizado";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const getDashboardCacheKey = ({ idClienteAipim }) =>
  `corretor_dashboard:v2:${idClienteAipim || "sem-cliente"}`;

const sortSituacoes = (items) =>
  [...items].sort((current, next) => {
    const currentOrder = current?.ordem ?? Number.POSITIVE_INFINITY;
    const nextOrder = next?.ordem ?? Number.POSITIVE_INFINITY;
    if (currentOrder !== nextOrder) return currentOrder - nextOrder;
    return Number(current?.idSituacao ?? 0) - Number(next?.idSituacao ?? 0);
  });

const getLeadSituacaoNome = (lead, situacoes = []) => {
  const directName =
    lead?.situacao ||
    lead?.nomeSituacao ||
    lead?.situacaoNome ||
    lead?.etapa ||
    lead?.nomeEtapa;

  if (directName) return directName;

  const idSituacao = lead?.idSituacao ?? lead?.id_situacao ?? lead?.idsituacao;
  const matched = situacoes.find((situacao) => Number(situacao.idSituacao) === Number(idSituacao));
  return matched?.nome || "";
};

const getWhatsappQuality = (value) => {
  const quality = String(value || "NOT_AVAILABLE").toUpperCase();
  const options = {
    GREEN: { label: "Qualidade boa", color: "success" },
    YELLOW: { label: "Qualidade media", color: "warning" },
    RED: { label: "Qualidade baixa", color: "error" },
    UNKNOWN: { label: "Qualidade indisponivel", color: "default" },
    NOT_AVAILABLE: { label: "Nao configurado", color: "default" },
  };
  return options[quality] || options.UNKNOWN;
};

const leadDetailValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) {
    return value
      .map((item) => leadDetailValue(item))
      .filter((item) => item !== "-")
      .join(", ") || "-";
  }
  if (typeof value === "object") {
    return value.nome || value.name || value.descricao || value.empreendimento || value.id || "-";
  }
  return String(value);
};

const LeadDetailField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
      {leadDetailValue(value)}
    </Typography>
  </Box>
);

const CorretorMetricCard = ({ group, label, value, tone = "primary", icon: Icon }) => {
  const theme = useTheme();
  const toneColor = tone === "danger"
    ? theme.palette.error.main
    : tone === "muted"
      ? theme.palette.text.secondary
      : theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        minHeight: 98,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        borderTop: "3px solid",
        borderTopColor: toneColor,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} minWidth={0}>
        <Box
          sx={{
            width: 26,
            height: 26,
            display: "grid",
            placeItems: "center",
            borderRadius: 1,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "action.hover",
            color: toneColor,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={800} noWrap>
          {group}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        fontWeight={800}
        sx={{
          mt: 1.25,
          color: "text.secondary",
          lineHeight: 1.25,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ mt: 0.55, color: "text.primary", fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const getCachedDashboard = (cacheKey) => {
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    return cached?.data ? cached : null;
  } catch (error) {
    return null;
  }
};

const setCachedDashboard = (cacheKey, data) => {
  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        cachedAt: new Date().toISOString(),
      })
    );
  } catch (error) {
    // Cache local indisponivel nao deve bloquear o dashboard.
  }
};

const mapLiviaMessage = (item) => ({
  id: item?.id || `${item?.role || "message"}-${item?.dataHoraCadastro || Date.now()}`,
  role: item?.role === "assistant" ? "assistant" : "user",
  text: item?.texto || "",
  createdAt: item?.dataHoraCadastro || "",
});

const liviaProfileSrc = "/livia-profile.png";

function LiviaFab({ buttonRef, onClick, onPointerDown, position, isDragging }) {
  return (
    <Button
      ref={buttonRef}
      onClick={onClick}
      onPointerDown={onPointerDown}
      aria-label="Abrir assistente Livia"
      title="Arraste para reposicionar"
      sx={{
        position: "fixed",
        ...(position
          ? { left: position.left, top: position.top }
          : { right: { xs: LIVIA_FAB_MARGIN, md: 28 }, bottom: { xs: LIVIA_FAB_MARGIN, md: LIVIA_INITIAL_BOTTOM } }),
        minWidth: 0,
        height: 58,
        px: { xs: 1.3, sm: 1.8 },
        borderRadius: 999,
        bgcolor: "#315eea",
        color: "#fff",
        boxShadow: "0 18px 40px rgba(0,20,50,0.28)",
        textTransform: "none",
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        "&:hover": {
          bgcolor: "#244ac5",
          boxShadow: "0 20px 44px rgba(0,20,50,0.32)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Avatar src={liviaProfileSrc} alt="Livia" sx={{ width: 34, height: 34, bgcolor: "#fff", color: "#315eea", fontWeight: 900 }}>
          L
        </Avatar>
        <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900, lineHeight: 1.1, color: "#fff" }}>
            Livia
          </Typography>
          <Typography sx={{ fontSize: 11, lineHeight: 1.1, color: "rgba(255,255,255,0.72)" }}>
            Agente do corretor
          </Typography>
        </Box>
      </Stack>
    </Button>
  );
}

function LiviaPanel({ onClose, docked = false, messages = [], loading = false, error = "", onRefresh, dragHandleProps }) {
  const { sx: dragHandleSx, ...resolvedDragHandleProps } = dragHandleProps || {};
  const [question, setQuestion] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [messages, loading, error]);

  const clearConversation = () => {
    setQuestion("");
    onRefresh?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: docked ? "none" : "0 16px 42px rgba(0,20,50,0.22)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        {...resolvedDragHandleProps}
        sx={{
          px: 2,
          py: 1.4,
          borderBottom: "1px solid",
          borderColor: "divider",
          cursor: dragHandleProps ? "move" : "default",
          touchAction: dragHandleProps ? "none" : undefined,
          userSelect: dragHandleProps ? "none" : undefined,
          ...dragHandleSx,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Avatar src={liviaProfileSrc} alt="Livia" sx={{ width: 40, height: 40, bgcolor: "#315eea", color: "#fff", fontWeight: 900 }}>L</Avatar>
          <Box>
            <Typography fontWeight={900}>Livia</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11 }}>Agente do corretor</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={clearConversation} aria-label="Nova conversa com Livia">
            <RestartAltRounded fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Fechar assistente Livia">
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Stack spacing={1.2} sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>Carregando conversas...</Typography>
          </Stack>
        )}
        {!loading && error && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && messages.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: "background.default",
            }}
          >
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
              As ultimas conversas serao carregadas aqui.
            </Typography>
          </Paper>
        )}
        {messages.map((item) => {
          const isUser = item.role === "user";
          return (
            <Stack key={item.id} direction="row" justifyContent={isUser ? "flex-end" : "flex-start"}>
              {!isUser && (
                <Box sx={{ width: 30, height: 30, mr: 1, borderRadius: "50%", bgcolor: "#0da879", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <SmartToyOutlined sx={{ fontSize: 17 }} />
                </Box>
              )}
              <Paper
                elevation={0}
                sx={{
                  maxWidth: "82%",
                  p: 1.35,
                  bgcolor: isUser ? "#315eea" : "action.hover",
                  color: isUser ? "#fff" : "text.primary",
                  borderRadius: 2.4,
                  border: isUser ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  {item.text}
                </Typography>
                {item.createdAt && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 10.5,
                      opacity: 0.68,
                      textAlign: isUser ? "right" : "left",
                    }}
                  >
                    {formatDateTime(item.createdAt)}
                  </Typography>
                )}
              </Paper>
            </Stack>
          );
        })}
        <Box ref={messagesEndRef} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, mt: 1 }}>
        <InputBase
          value={question}
          onChange={(event) => setQuestion(event.target.value.slice(0, 1000))}
          onKeyDown={handleKeyDown}
          placeholder="Envio pelo portal em breve"
          disabled
          multiline
          maxRows={3}
          sx={{ flex: 1, minHeight: 44, px: 1.8, py: 0.8, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
        />
        <IconButton
          disabled
          sx={{ width: 44, height: 44, bgcolor: "#0da879", color: "#fff", "&:hover": { bgcolor: "#087765" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled" } }}
        >
          <SendRounded />
        </IconButton>
      </Stack>
    </Paper>
  );
}

const AreaCorretor = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [searchParams] = useSearchParams();
  const liviaWidgetRef = useRef(null);
  const liviaDragRef = useRef(null);
  const liviaFabRef = useRef(null);
  const liviaFabDragRef = useRef(null);
  const suppressLiviaFabClickRef = useRef(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedLeadSearch, setAppliedLeadSearch] = useState("");
  const [leadSituacaoId, setLeadSituacaoId] = useState("");
  const [appliedLeadSituacaoId, setAppliedLeadSituacaoId] = useState("");
  const [leadSituacoes, setLeadSituacoes] = useState([]);
  const [loadingLeadSituacoes, setLoadingLeadSituacoes] = useState(false);
  const [leadViewMode, setLeadViewMode] = useState("list");
  const [leadOffset, setLeadOffset] = useState(0);
  const [liviaOpen, setLiviaOpen] = useState(false);
  const [liviaWidgetPosition, setLiviaWidgetPosition] = useState(null);
  const [isDraggingLivia, setIsDraggingLivia] = useState(false);
  const [liviaFabPosition, setLiviaFabPosition] = useState(getStoredLiviaFabPosition);
  const [isDraggingLiviaFab, setIsDraggingLiviaFab] = useState(false);
  const corretor = useMemo(() => getCorretorFromToken(), []);
  const dashboardCacheKey = useMemo(
    () =>
      getDashboardCacheKey({
        idClienteAipim: corretor.idClienteAipim,
      }),
    [corretor.idClienteAipim]
  );
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardCachedAt, setDashboardCachedAt] = useState("");
  const [dashboardError, setDashboardError] = useState("");
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loadingWhatsappStatus, setLoadingWhatsappStatus] = useState(true);
  const [refreshingWhatsappStatus, setRefreshingWhatsappStatus] = useState(false);
  const [whatsappStatusError, setWhatsappStatusError] = useState("");
  const [liviaMessages, setLiviaMessages] = useState([]);
  const [loadingLiviaMessages, setLoadingLiviaMessages] = useState(false);
  const [liviaMessagesError, setLiviaMessagesError] = useState("");
  const [leadsData, setLeadsData] = useState({ limit: LEADS_LIMIT, offset: 0, totalRetornado: 0, itens: [] });
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadsError, setLeadsError] = useState("");
  const [hasLeadSearchRun, setHasLeadSearchRun] = useState(false);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [leadDetailSummary, setLeadDetailSummary] = useState(null);
  const [leadDetailData, setLeadDetailData] = useState(null);
  const [loadingLeadDetail, setLoadingLeadDetail] = useState(false);
  const [leadDetailError, setLeadDetailError] = useState("");
  const leadsKnownTotal = useMemo(() => {
    if (appliedLeadSearch.trim()) return null;
    if (appliedLeadSituacaoId) return null;

    return dashboardData?.leads?.totalAtivos ?? null;
  }, [
    appliedLeadSearch,
    appliedLeadSituacaoId,
    dashboardData?.leads?.totalAtivos,
  ]);

  const tabs = useMemo(
    () => [
      { id: "dashboard", label: "Dashboard", icon: BarChartRounded },
      { id: "leads", label: "Leads", icon: SearchRounded },
      { id: "conversas-leads", label: "Converse com seus Leads", icon: ChatBubbleOutlineRounded },
    ],
    []
  );

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (tabs.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams, tabs]);

  useEffect(() => {
    if (searchParams.get("chat") === "livia") {
      setActiveTab("dashboard");
      setLiviaOpen(true);
    }
  }, [searchParams]);

  const loadDashboard = useCallback(
    async ({ forceRefresh = false } = {}) => {
      const cached = getCachedDashboard(dashboardCacheKey);

      if (cached && !forceRefresh) {
        setDashboardData(cached.data);
        setDashboardCachedAt(cached.cachedAt || "");
        setLoadingDashboard(false);
        return;
      }

      try {
        if (forceRefresh) {
          setRefreshingDashboard(true);
        } else {
          setLoadingDashboard(true);
        }

        setDashboardError("");

        const data = await corretorDashboardService.getDashboard();

        setDashboardData(data);
        setDashboardCachedAt(new Date().toISOString());
        setCachedDashboard(dashboardCacheKey, data);
      } catch (err) {
        setDashboardError(
          corretorDashboardService.getApiErrorMessage(err, "Erro ao carregar dashboard do corretor.")
        );

        if (cached) {
          setDashboardData(cached.data);
          setDashboardCachedAt(cached.cachedAt || "");
        }
      } finally {
        setLoadingDashboard(false);
        setRefreshingDashboard(false);
      }
    },
    [dashboardCacheKey]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadWhatsappStatus = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      if (forceRefresh) setRefreshingWhatsappStatus(true);
      else setLoadingWhatsappStatus(true);
      setWhatsappStatusError("");
      setWhatsappStatus(await corretorDashboardService.getWhatsappStatus({ forceRefresh }));
    } catch (error) {
      setWhatsappStatusError(
        corretorDashboardService.getApiErrorMessage(error, "Nao foi possivel consultar o WhatsApp em uso.")
      );
    } finally {
      setLoadingWhatsappStatus(false);
      setRefreshingWhatsappStatus(false);
    }
  }, []);

  useEffect(() => {
    loadWhatsappStatus();
  }, [loadWhatsappStatus]);

  useEffect(() => {
    // Antecipamos o cache sem bloquear o dashboard; Leads e aiDirect reutilizam a mesma requisicao.
    corretorDashboardService.getSituacoes().catch(() => {});
  }, []);

  const loadLeadSituacoes = useCallback(async () => {
    try {
      setLoadingLeadSituacoes(true);
      const data = await corretorDashboardService.getSituacoes();
      setLeadSituacoes(sortSituacoes(data));
    } catch (err) {
      setLeadsError(
        corretorDashboardService.getApiErrorMessage(err, "Erro ao carregar situacoes do CVCRM.")
      );
    } finally {
      setLoadingLeadSituacoes(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "leads" || leadSituacoes.length) return;
    loadLeadSituacoes();
  }, [activeTab, leadSituacoes.length, loadLeadSituacoes]);

  const loadLiviaMessages = useCallback(async () => {
    const idCorretor = dashboardData?.idCorretorCv;

    if (!idCorretor) {
      setLiviaMessages([]);
      setLiviaMessagesError("Dados do corretor ainda nao disponiveis para carregar as conversas.");
      return;
    }

    try {
      setLoadingLiviaMessages(true);
      setLiviaMessagesError("");

      const data = await corretorDashboardService.getMensagens({
        idCorretor,
        limite: 200,
      });

      setLiviaMessages(data.map(mapLiviaMessage).filter((item) => item.text));
    } catch (err) {
      setLiviaMessagesError(
        corretorDashboardService.getApiErrorMessage(err, "Erro ao carregar conversas com a Livia.")
      );
    } finally {
      setLoadingLiviaMessages(false);
    }
  }, [dashboardData?.idCorretorCv]);

  useEffect(() => {
    if (!liviaOpen) return;
    loadLiviaMessages();
  }, [liviaOpen, loadLiviaMessages]);

  const loadLeads = useCallback(async (options = {}) => {
    const nextBusca = options.busca ?? appliedLeadSearch;
    const nextSituacaoId = options.idSituacao ?? appliedLeadSituacaoId;
    const nextOffset = options.offset ?? leadOffset;
    const idCorretor = dashboardData?.idCorretorCv;

    if (!idCorretor) {
      setLeadsData({ limit: LEADS_LIMIT, offset: nextOffset, totalRetornado: 0, itens: [] });
      setLeadsError("Dados do corretor ainda nao disponiveis para carregar leads.");
      return;
    }

    try {
      setLoadingLeads(true);
      setLeadsError("");
      setHasLeadSearchRun(true);

      const data = await corretorDashboardService.getLeads({
        idCorretor,
        busca: nextBusca.trim() || undefined,
        idSituacao: nextSituacaoId || undefined,
        limit: LEADS_LIMIT,
        offset: nextOffset,
      });

      setLeadsData({
        limit: data?.limit ?? LEADS_LIMIT,
        offset: data?.offset ?? nextOffset,
        totalRetornado: data?.totalRetornado ?? 0,
        itens: Array.isArray(data?.itens) ? data.itens : [],
      });
    } catch (err) {
      setLeadsError(corretorDashboardService.getApiErrorMessage(err, "Erro ao carregar leads."));
      setLeadsData({ limit: LEADS_LIMIT, offset: nextOffset, totalRetornado: 0, itens: [] });
    } finally {
      setLoadingLeads(false);
    }
  }, [appliedLeadSearch, appliedLeadSituacaoId, dashboardData?.idCorretorCv, leadOffset]);

  const handleLeadSearchSubmit = useCallback(() => {
    const nextSearch = searchTerm.trim();
    setLeadOffset(0);
    setAppliedLeadSearch(nextSearch);
    setAppliedLeadSituacaoId(leadSituacaoId);
    setHasLeadSearchRun(true);
    loadLeads({ busca: nextSearch, idSituacao: leadSituacaoId, offset: 0 });
  }, [leadSituacaoId, loadLeads, searchTerm]);

  const handleDashboardSituacaoClick = useCallback((idSituacao) => {
    if (!idSituacao) return;

    const nextSituacaoId = String(idSituacao);
    setActiveTab("leads");
    setSearchTerm("");
    setAppliedLeadSearch("");
    setLeadSituacaoId(nextSituacaoId);
    setAppliedLeadSituacaoId(nextSituacaoId);
    setLeadOffset(0);
    setHasLeadSearchRun(true);
    loadLeads({ busca: "", idSituacao: nextSituacaoId, offset: 0 });
  }, [loadLeads]);

  const handleLeadPageChange = useCallback((nextOffset) => {
    setLeadOffset(nextOffset);
    loadLeads({ offset: nextOffset });
  }, [loadLeads]);

  const handleClearLeadFilters = useCallback(() => {
    setSearchTerm("");
    setLeadSituacaoId("");
    setAppliedLeadSearch("");
    setAppliedLeadSituacaoId("");
    setLeadOffset(0);
    setLeadsError("");
    setHasLeadSearchRun(false);
    setLeadsData({ limit: LEADS_LIMIT, offset: 0, totalRetornado: 0, itens: [] });
  }, []);

  const handleOpenLeadDetail = useCallback(async (lead) => {
    const idLead = lead?.idLead ?? lead?.idlead;
    if (!idLead) return;

    setLeadDetailOpen(true);
    setLeadDetailSummary(lead);
    setLeadDetailData(null);
    setLeadDetailError("");
    setLoadingLeadDetail(true);

    try {
      setLeadDetailData(await corretorDashboardService.getLeadDetails({ idLead }));
    } catch (error) {
      setLeadDetailError(
        corretorDashboardService.getApiErrorMessage(error, "Nao foi possivel carregar os detalhes do lead.")
      );
    } finally {
      setLoadingLeadDetail(false);
    }
  }, []);

  const handleLiviaFabDragStart = useCallback((event) => {
    if (event.button !== 0) return;

    const rect = liviaFabRef.current?.getBoundingClientRect();
    if (!rect) return;

    event.preventDefault();
    liviaFabDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
      lastPosition: { left: rect.left, top: rect.top },
    };
    setIsDraggingLiviaFab(true);
  }, []);

  const handleLiviaFabClick = useCallback(() => {
    if (suppressLiviaFabClickRef.current) {
      suppressLiviaFabClickRef.current = false;
      return;
    }
    setLiviaOpen(true);
  }, []);

  useEffect(() => {
    if (!isDraggingLiviaFab) return undefined;

    const handlePointerMove = (event) => {
      const drag = liviaFabDragRef.current;
      if (!drag) return;

      const maxLeft = window.innerWidth - drag.width - LIVIA_FAB_MARGIN;
      const maxTop = window.innerHeight - drag.height - LIVIA_FAB_MARGIN;
      const position = {
        left: clamp(drag.startLeft + event.clientX - drag.startX, LIVIA_FAB_MARGIN, Math.max(LIVIA_FAB_MARGIN, maxLeft)),
        top: clamp(drag.startTop + event.clientY - drag.startY, LIVIA_FAB_MARGIN, Math.max(LIVIA_FAB_MARGIN, maxTop)),
      };
      drag.moved = drag.moved || Math.abs(event.clientX - drag.startX) > 5 || Math.abs(event.clientY - drag.startY) > 5;
      drag.lastPosition = position;
      setLiviaFabPosition(position);
    };

    const handlePointerUp = () => {
      const drag = liviaFabDragRef.current;
      if (drag?.moved) {
        suppressLiviaFabClickRef.current = true;
        setStoredLiviaFabPosition(drag.lastPosition);
        window.setTimeout(() => {
          suppressLiviaFabClickRef.current = false;
        }, 0);
      }
      liviaFabDragRef.current = null;
      setIsDraggingLiviaFab(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("blur", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("blur", handlePointerUp);
    };
  }, [isDraggingLiviaFab]);

  useEffect(() => {
    if (!liviaFabPosition) return undefined;

    const handleResize = () => {
      const rect = liviaFabRef.current?.getBoundingClientRect();
      if (!rect) return;

      setLiviaFabPosition((current) => {
        if (!current) return current;
        const position = {
          left: clamp(current.left, LIVIA_FAB_MARGIN, Math.max(LIVIA_FAB_MARGIN, window.innerWidth - rect.width - LIVIA_FAB_MARGIN)),
          top: clamp(current.top, LIVIA_FAB_MARGIN, Math.max(LIVIA_FAB_MARGIN, window.innerHeight - rect.height - LIVIA_FAB_MARGIN)),
        };
        setStoredLiviaFabPosition(position);
        return position;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [liviaFabPosition]);

  const handleLiviaDragStart = useCallback(
    (event) => {
      if (!isDesktop || event.button !== 0) return;
      if (event.target.closest("button,input,textarea,a")) return;

      const rect = liviaWidgetRef.current?.getBoundingClientRect();
      if (!rect) return;

      event.preventDefault();
      liviaDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft: rect.left,
        startTop: rect.top,
        width: rect.width,
        height: rect.height,
      };
      setIsDraggingLivia(true);
    },
    [isDesktop]
  );

  useEffect(() => {
    if (!isDraggingLivia) return undefined;

    const handlePointerMove = (event) => {
      const drag = liviaDragRef.current;
      if (!drag) return;

      const maxLeft = window.innerWidth - drag.width - LIVIA_WIDGET_MARGIN;
      const maxTop = window.innerHeight - drag.height - LIVIA_WIDGET_MARGIN;
      setLiviaWidgetPosition({
        left: clamp(drag.startLeft + event.clientX - drag.startX, LIVIA_WIDGET_MARGIN, Math.max(LIVIA_WIDGET_MARGIN, maxLeft)),
        top: clamp(drag.startTop + event.clientY - drag.startY, LIVIA_WIDGET_MARGIN, Math.max(LIVIA_WIDGET_MARGIN, maxTop)),
      });
    };

    const handlePointerUp = () => {
      liviaDragRef.current = null;
      setIsDraggingLivia(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("blur", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("blur", handlePointerUp);
    };
  }, [isDraggingLivia]);

  useEffect(() => {
    if (!liviaWidgetPosition) return undefined;

    const handleResize = () => {
      const rect = liviaWidgetRef.current?.getBoundingClientRect();
      if (!rect) return;

      setLiviaWidgetPosition((current) => {
        if (!current) return current;
        return {
          left: clamp(current.left, LIVIA_WIDGET_MARGIN, Math.max(LIVIA_WIDGET_MARGIN, window.innerWidth - rect.width - LIVIA_WIDGET_MARGIN)),
          top: clamp(current.top, LIVIA_WIDGET_MARGIN, Math.max(LIVIA_WIDGET_MARGIN, window.innerHeight - rect.height - LIVIA_WIDGET_MARGIN)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [liviaWidgetPosition]);

  const overviewMetrics = [
    {
      group: "Leads",
      label: "Total de leads ativos",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.leads?.totalAtivos),
      tone: "primary",
      icon: BarChartRounded,
    },
    {
      group: "Leads",
      label: "Leads com tarefas vencidas",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.leads?.comTarefasVencidas),
      tone: "danger",
      icon: TrendingUpRounded,
    },
    {
      group: "Tarefas",
      label: "Tarefas vencidas",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.tarefas?.vencidas),
      tone: "danger",
      icon: TrendingUpRounded,
    },
    {
      group: "Tarefas",
      label: "Hoje",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.tarefas?.hoje),
      tone: "primary",
      icon: BarChartRounded,
    },
    {
      label: "Amanhã",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.tarefas?.amanha),
      group: "Tarefas",
      tone: "muted",
      icon: BarChartRounded,
    },
    {
      label: "Próximos 3 dias",
      value: loadingDashboard ? "..." : formatNumber(dashboardData?.tarefas?.proximos3Dias),
      group: "Tarefas",
      tone: "muted",
      icon: RefreshRounded,
    },
  ];

  const detailLead = leadDetailData || leadDetailSummary;
  const detailLeadId = detailLead?.idlead ?? detailLead?.idLead;
  const detailSituacao = leadDetailData?.situacao;
  const whatsappQuality = getWhatsappQuality(whatsappStatus?.qualidadeConta);

  return (
    <Box
      sx={{
        minHeight: { xs: "calc(100dvh - 104px)", md: "calc(100vh - 112px)" },
        mx: { xs: -2, sm: 0 },
        bgcolor: theme.palette.mode === "light" ? "#eef1f5" : "background.default",
      }}
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 0 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              Área do Corretor
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Olá, {corretor.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Acompanhe seus atendimentos e converse com o agente em um só lugar.
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mx: { xs: 2, sm: 0 },
            px: { xs: 0.7, md: 1 },
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabs.findIndex((tab) => tab.id === activeTab)}
            onChange={(_, nextIndex) => setActiveTab(tabs[nextIndex]?.id || "dashboard")}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Navegacao da Area do Corretor"
            sx={{
              minHeight: 52,
              "& .MuiTabs-indicator": {
                height: 2,
                borderRadius: 99,
              },
              "& .MuiTab-root": {
                minHeight: 52,
                px: { xs: 2, md: 2.8 },
                textTransform: "none",
                fontWeight: 800,
                fontSize: { xs: 13, md: 14 },
                color: "text.secondary",
                gap: 0.8,
              },
              "& .MuiTab-root.Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            {tabs.map((item) => {
              const Icon = item.icon;
              return <Tab key={item.id} icon={<Icon sx={{ fontSize: 18 }} />} iconPosition="start" label={item.label} />;
            })}
          </Tabs>
        </Paper>

        {activeTab === "dashboard" ? (
          <Stack spacing={2} sx={{ px: { xs: 2, sm: 0 } }}>
            <Box
              sx={{
                px: 0.25,
                display: "flex",
                alignItems: "center",
                justifyContent: loadingDashboard && !dashboardData ? "space-between" : "flex-end",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              {loadingDashboard && !dashboardData && (
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Buscando dados do CV...
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ minWidth: 0 }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  CV: {formatDateTime(dashboardData?.geradoEm)}
                </Typography>
                {dashboardCachedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Cache: {formatDateTime(dashboardCachedAt)}
                  </Typography>
                )}
              </Stack>
              <Button
                size="small"
                variant="text"
                startIcon={refreshingDashboard ? <CircularProgress size={14} /> : <RefreshRounded fontSize="small" />}
                onClick={() => loadDashboard({ forceRefresh: true })}
                disabled={refreshingDashboard}
                sx={{
                  minHeight: 30,
                  px: 1,
                  fontWeight: 800,
                }}
              >
                Atualizar
              </Button>
            </Box>

            {dashboardError && (
              <Alert severity={dashboardData ? "warning" : "error"} sx={{ borderRadius: 2 }}>
                {dashboardError}
              </Alert>
            )}

            <Box>
              <Typography variant="h6" fontWeight={900}>
                Visao geral
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Acompanhe seus leads e tarefas que pedem atencao.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(6, minmax(0, 1fr))",
                  },
                  gap: { xs: 1, sm: 1.25 },
                  mt: 1.5,
                }}
              >
                {overviewMetrics.map((metric) => <CorretorMetricCard key={`${metric.group}-${metric.label}`} {...metric} />)}
              </Box>
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={900}>
                Leads por situacao
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Distribuicao dos leads ativos nas etapas atuais do workflow.
              </Typography>

              {loadingDashboard ? (
                <Box sx={{ minHeight: 104, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : dashboardData?.leads?.porSituacao?.length ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: "repeat(5, minmax(0, 1fr))",
                      lg: "repeat(6, minmax(0, 1fr))",
                      xl: "repeat(7, minmax(0, 1fr))",
                    },
                    gap: { xs: 1, sm: 1.25 },
                    mt: 1.5,
                  }}
                >
                  {dashboardData.leads.porSituacao.map((situacao, index) => (
                    <Paper
                      key={situacao.idSituacao ?? `${situacao.nome}-${index}`}
                      component="button"
                      type="button"
                      onClick={() => handleDashboardSituacaoClick(situacao.idSituacao)}
                      aria-label={`Ver leads na situacao ${situacao.nome || situacao.idSituacao}`}
                      elevation={0}
                      sx={{
                        minWidth: 0,
                        minHeight: 80,
                        p: 1.25,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        textAlign: "left",
                        font: "inherit",
                        cursor: "pointer",
                        transition: "border-color 150ms ease, background-color 150ms ease, transform 150ms ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                          transform: "translateY(-1px)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={800}
                        sx={{
                          lineHeight: 1.25,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {situacao.nome || "Situacao nao informada"}
                      </Typography>
                      <Typography color="text.primary" fontWeight={900} sx={{ mt: 1, fontSize: 22, lineHeight: 1 }}>
                        {formatNumber(situacao.totalLeads)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Nenhum lead ativo associado a uma situacao no momento.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "stretch",
                justifyContent: "center",
                gap: 1.25,
                "& > .MuiPaper-root": {
                  width: { xs: "100%", sm: 340 },
                  maxWidth: "100%",
                },
              }}
            >
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1, display: "grid", placeItems: "center", bgcolor: "rgba(37, 211, 102, 0.14)", color: "#128c4a", flexShrink: 0 }}>
                      <WhatsApp fontSize="small" />
                    </Box>
                    <Typography fontWeight={800} noWrap>WhatsApp em uso</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {!loadingWhatsappStatus && !whatsappStatusError && <Chip size="small" color={whatsappQuality.color} label={whatsappQuality.label} />}
                    <Tooltip title="Verificar status do WhatsApp">
                      <span>
                        <IconButton aria-label="Verificar status do WhatsApp" size="small" onClick={() => loadWhatsappStatus({ forceRefresh: true })} disabled={loadingWhatsappStatus || refreshingWhatsappStatus}>
                          {refreshingWhatsappStatus ? <CircularProgress size={18} /> : <RefreshRounded fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflowWrap: "anywhere" }}>
                  {loadingWhatsappStatus
                    ? "Consultando configuracao..."
                    : whatsappStatus?.configurado
                      ? `${whatsappStatus.nomePerfil || "Perfil sem nome"} - ${whatsappStatus.telefone ? `+${whatsappStatus.telefone}` : "Telefone nao informado"}`
                      : "Nenhum WhatsApp configurado para envio."}
                </Typography>
                {whatsappStatusError && <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>{whatsappStatusError}</Typography>}
                {!loadingWhatsappStatus && whatsappStatus?.consultadoEm && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Consultado em {formatDateTime(whatsappStatus.consultadoEm)}</Typography>}
              </Paper>

              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                <Typography fontWeight={800}>Vinculo CVCRM</Typography>
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>CORRETOR CV</Typography>
                    <Typography variant="body2" fontWeight={800}>{dashboardData?.idCorretorCv || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>IMOBILIARIA CV</Typography>
                    <Typography variant="body2" fontWeight={800}>{dashboardData?.idImobiliariaCv || "-"}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        ) : activeTab === "leads" ? (
          <Paper
            elevation={0}
            sx={{
              mx: { xs: 2, sm: 0 },
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Leads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pesquise leads vinculados ao seu atendimento.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Buscar por ID, nome, email, telefone, empreendimento ou situação"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleLeadSearchSubmit();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Situacao"
                  value={leadSituacaoId}
                  onChange={(event) => {
                    setLeadOffset(0);
                    setLeadSituacaoId(event.target.value);
                  }}
                  disabled={loadingLeadSituacoes}
                  sx={{ minWidth: { xs: "100%", md: 240 } }}
                >
                  <MenuItem value="">Todas as situacoes</MenuItem>
                  {leadSituacoes.map((situacao) => (
                    <MenuItem key={situacao.idSituacao} value={String(situacao.idSituacao)}>
                      {situacao.nome}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  startIcon={<SearchRounded />}
                  onClick={handleLeadSearchSubmit}
                  disabled={loadingLeads}
                  sx={{ minHeight: 40, px: 2.2 }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltRounded />}
                  onClick={handleClearLeadFilters}
                  disabled={loadingLeads && !hasLeadSearchRun}
                  sx={{ minHeight: 40, px: 1.8, whiteSpace: "nowrap" }}
                >
                  Limpar
                </Button>
                <IconButton onClick={() => loadLeads()} disabled={loadingLeads || !hasLeadSearchRun} sx={{ alignSelf: { xs: "flex-end", md: "center" } }}>
                  {loadingLeads ? <CircularProgress size={20} /> : <RefreshRounded />}
                </IconButton>
              </Stack>

              {leadsError && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {leadsError}
                </Alert>
              )}

              {hasLeadSearchRun ? (
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Stack
                    direction="row"
                    spacing={0.25}
                    sx={{
                      p: 0.25,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "background.default",
                    }}
                  >
                    <IconButton
                      size="small"
                      title="Listagem"
                      aria-label="Visualizar em listagem"
                      onClick={() => setLeadViewMode("list")}
                      color={leadViewMode === "list" ? "primary" : "default"}
                      sx={{ width: 30, height: 30, borderRadius: 1 }}
                    >
                      <ViewListRounded fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Cards"
                      aria-label="Visualizar em cards"
                      onClick={() => setLeadViewMode("cards")}
                      color={leadViewMode === "cards" ? "primary" : "default"}
                      sx={{ width: 30, height: 30, borderRadius: 1 }}
                    >
                      <ViewModuleRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  {loadingLeads
                    ? "Carregando leads..."
                    : leadsKnownTotal !== null && leadsKnownTotal !== undefined
                      ? leadsData.itens.length > 0
                        ? `Exibindo ${leadOffset + 1}-${leadOffset + leadsData.itens.length} de ${formatNumber(leadsKnownTotal)} lead(s)`
                        : `0 de ${formatNumber(leadsKnownTotal)} lead(s)`
                      : `${leadsData.itens.length} lead(s) nesta pagina`}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  Página {Math.floor(leadOffset / LEADS_LIMIT) + 1}
                </Typography>
              </Stack>
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px dashed", borderColor: "divider", textAlign: "center" }}>
                  <Typography fontWeight={900}>Use a busca para localizar leads.</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clique em Buscar para listar os primeiros leads ou informe ID, email, telefone, nome, empreendimento ou situacao.
                  </Typography>
                </Paper>
              )}

              {hasLeadSearchRun && leadsData.itens.length > 0 && leadViewMode === "list" && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    bgcolor: "background.default",
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: "none", md: "grid" },
                      gridTemplateColumns: "120px minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(160px, 1fr) minmax(180px, 1fr)",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="caption" fontWeight={900}>Lead</Typography>
                    <Typography variant="caption" fontWeight={900}>Nome</Typography>
                    <Typography variant="caption" fontWeight={900}>Contato</Typography>
                    <Typography variant="caption" fontWeight={900}>Empreendimento</Typography>
                    <Typography variant="caption" fontWeight={900}>Etapa</Typography>
                  </Box>
                  {leadsData.itens.map((item) => {
                    const situacaoNome = getLeadSituacaoNome(item, leadSituacoes);
                    return (
                      <Box
                        key={item.idLead}
                        role="button"
                        tabIndex={0}
                        aria-label={`Ver detalhes de ${item.nome || `Lead ${item.idLead}`}`}
                        onClick={() => handleOpenLeadDetail(item)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleOpenLeadDetail(item);
                          }
                        }}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "120px minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(160px, 1fr) minmax(180px, 1fr)",
                          },
                          gap: { xs: 0.7, md: 1 },
                          alignItems: "center",
                          px: 1.5,
                          py: 1.2,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          "&:hover": { bgcolor: "action.hover" },
                          "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -2,
                          },
                          "&:last-of-type": { borderBottom: 0 },
                        }}
                      >
                        <Chip
                          size="small"
                          label={item.idLead}
                          color="primary"
                          variant="outlined"
                          sx={{ justifySelf: "flex-start" }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900} noWrap>
                            {item.nome || "Lead sem nome"}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {item.telefone || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {item.email || "-"}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {item.empreendimento || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            Cadastro: {item.dataCadastro || "-"}
                          </Typography>
                        </Box>
                        <Stack spacing={0.7} alignItems="flex-start" sx={{ minWidth: 0 }}>
                          <Chip
                            size="small"
                            label={situacaoNome || "Etapa nao informada"}
                            sx={{ fontWeight: 700, maxWidth: "100%" }}
                          />
                          {item.tarefa?.descricao && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                              {item.tarefa.descricao}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Paper>
              )}

              {hasLeadSearchRun && leadsData.itens.length > 0 && leadViewMode === "cards" && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {leadsData.itens.map((item) => {
                  const situacaoNome = getLeadSituacaoNome(item, leadSituacoes);
                  return (
                    <Paper
                      key={item.idLead}
                      elevation={0}
                      role="button"
                      tabIndex={0}
                      aria-label={`Ver detalhes de ${item.nome || `Lead ${item.idLead}`}`}
                      onClick={() => handleOpenLeadDetail(item)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOpenLeadDetail(item);
                        }
                      }}
                      sx={{
                        p: 1.6,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.default",
                        cursor: "pointer",
                        transition: "background-color 0.15s ease, border-color 0.15s ease",
                        "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Chip
                            size="small"
                            label={item.idLead}
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {item.dataCadastro || "-"}
                          </Typography>
                        </Stack>
                        <Box>
                          <Typography fontWeight={900} noWrap>
                            {item.nome || "Lead sem nome"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {item.empreendimento || "-"}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.telefone || item.email || "Contato nao informado"}
                        </Typography>
                        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                          <Chip
                            size="small"
                            label={situacaoNome || "Etapa nao informada"}
                            sx={{ fontWeight: 700, maxWidth: "100%" }}
                          />
                        </Stack>
                        {item.tarefa?.descricao && (
                          <Typography variant="caption" color="text.secondary">
                            Tarefa: {item.tarefa.descricao}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
              )}

              {hasLeadSearchRun && !loadingLeads && leadsData.itens.length === 0 && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid", borderColor: "divider", textAlign: "center" }}>
                  <Typography fontWeight={900}>Nenhum resultado encontrado.</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tente buscar por outro nome, empreendimento ou etapa.
                  </Typography>
                </Paper>
              )}

              {hasLeadSearchRun && (
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button
                  variant="outlined"
                  disabled={loadingLeads || leadOffset === 0}
                    onClick={() => handleLeadPageChange(Math.max(0, leadOffset - LEADS_LIMIT))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outlined"
                  disabled={
                    loadingLeads ||
                    leadsData.itens.length < LEADS_LIMIT ||
                    (leadsKnownTotal !== null && leadsKnownTotal !== undefined && leadOffset + leadsData.itens.length >= leadsKnownTotal)
                  }
                    onClick={() => handleLeadPageChange(leadOffset + LEADS_LIMIT)}
                >
                  Próxima
                </Button>
              </Stack>
              )}
            </Stack>
          </Paper>
        ) : activeTab === "conversas-leads" ? (
          <AiDirectCorretor />
        ) : null}
      </Stack>

      <Dialog
        open={leadDetailOpen}
        onClose={() => !loadingLeadDetail && setLeadDetailOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>
          Detalhes do lead
          {detailLeadId && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              #{detailLeadId}
            </Typography>
          )}
          <IconButton
            aria-label="Fechar detalhes do lead"
            onClick={() => setLeadDetailOpen(false)}
            disabled={loadingLeadDetail}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingLeadDetail && (
            <Box sx={{ minHeight: 220, display: "grid", placeItems: "center", gap: 1 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Carregando detalhes do lead...</Typography>
            </Box>
          )}

          {!loadingLeadDetail && leadDetailError && (
            <Alert severity="error">{leadDetailError}</Alert>
          )}

          {!loadingLeadDetail && !leadDetailError && leadDetailData && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  {leadDetailData.nome || "Lead sem nome"}
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {detailLeadId && <Chip size="small" color="primary" variant="outlined" label={`Lead ${detailLeadId}`} />}
                  {detailSituacao && <Chip size="small" label={leadDetailValue(detailSituacao)} sx={{ fontWeight: 700 }} />}
                </Stack>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                <LeadDetailField label="Telefone" value={leadDetailData.telefone} />
                <LeadDetailField label="E-mail" value={leadDetailData.email} />
                <LeadDetailField label="Origem" value={leadDetailData.origem} />
                <LeadDetailField label="Data de cadastro" value={leadDetailData.data_cad ?? leadDetailData.dataCadastro} />
                <LeadDetailField label="Score" value={leadDetailData.score} />
                <LeadDetailField label="Empreendimento" value={leadDetailData.empreendimento} />
              </Box>

              <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1.25 }}>Responsáveis</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                  <LeadDetailField label="Corretor" value={leadDetailData.corretor?.nome} />
                  <LeadDetailField label="E-mail do corretor" value={leadDetailData.corretor?.email} />
                  <LeadDetailField label="Imobiliária" value={leadDetailData.imobiliaria?.nome} />
                </Box>
              </Box>

              {(leadDetailData.link_interacoes || leadDetailData.link_simulacoes || leadDetailData.link_reservas) && (
                <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1.25 }}>Acessos do CVCRM</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {leadDetailData.link_interacoes && <Button component="a" href={leadDetailData.link_interacoes} target="_blank" rel="noreferrer" variant="outlined">Interações</Button>}
                    {leadDetailData.link_simulacoes && <Button component="a" href={leadDetailData.link_simulacoes} target="_blank" rel="noreferrer" variant="outlined">Simulações</Button>}
                    {leadDetailData.link_reservas && <Button component="a" href={leadDetailData.link_reservas} target="_blank" rel="noreferrer" variant="outlined">Reservas</Button>}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {!liviaOpen && (
        <LiviaFab
          buttonRef={liviaFabRef}
          onClick={handleLiviaFabClick}
          onPointerDown={handleLiviaFabDragStart}
          position={liviaFabPosition}
          isDragging={isDraggingLiviaFab}
        />
      )}

      {isDesktop && liviaOpen && (
        <Box
          ref={liviaWidgetRef}
          sx={{
            position: "fixed",
            ...(liviaWidgetPosition
              ? { left: liviaWidgetPosition.left, top: liviaWidgetPosition.top }
              : { right: LIVIA_WIDGET_MARGIN, bottom: LIVIA_INITIAL_BOTTOM }),
            width: 400,
            maxWidth: "calc(100vw - 56px)",
            height: "min(640px, calc(100vh - 96px))",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 22px 60px rgba(0,20,50,0.26)",
            transition: isDraggingLivia ? "none" : "box-shadow 0.18s ease",
          }}
        >
          <LiviaPanel
            onClose={() => setLiviaOpen(false)}
            docked
            messages={liviaMessages}
            loading={loadingLiviaMessages}
            error={liviaMessagesError}
            onRefresh={loadLiviaMessages}
            dragHandleProps={{
              onPointerDown: handleLiviaDragStart,
              title: "Arraste para mover",
            }}
          />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={!isDesktop && liviaOpen}
        onClose={() => setLiviaOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            display: { md: "none" },
            width: { xs: "100%", sm: 390 },
            maxWidth: "100vw",
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <LiviaPanel
          onClose={() => setLiviaOpen(false)}
          messages={liviaMessages}
          loading={loadingLiviaMessages}
          error={liviaMessagesError}
          onRefresh={loadLiviaMessages}
        />
      </Drawer>
    </Box>
  );
};

export default AreaCorretor;
